/**
 * AssignTransportModal Component
 * Modal for assigning transport to an order with transport unit selection
 *
 * @author SAUBHAGYA Development Team
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Truck, MapPin, Phone, User, Package, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { orderService, TransportUnit, CreateTransportRequestData } from '@/services/commerce/orderService';
import type { OrderDetails } from '@/types/order';

interface AssignTransportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderDetails;
  onSuccess: (updatedOrder: OrderDetails) => void;
}

export function AssignTransportModal({
  open,
  onOpenChange,
  order,
  onSuccess,
}: AssignTransportModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [transportUnits, setTransportUnits] = useState<TransportUnit[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [pickupAddress, setPickupAddress] = useState('Biogas Plant, Warehouse A, Industrial Area');
  const [pickupCity, setPickupCity] = useState('');
  const [pickupState, setPickupState] = useState('');
  const [pickupPincode, setPickupPincode] = useState('');
  const [pickupContactName, setPickupContactName] = useState('Plant Manager');
  const [pickupContactPhone, setPickupContactPhone] = useState('+91-9876543210');
  const [dropAddress, setDropAddress] = useState('');
  const [dropContactName, setDropContactName] = useState('');
  const [dropContactPhone, setDropContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);

  // Load transport units when modal opens
  useEffect(() => {
    if (open) {
      fetchTransportUnits();
      // Pre-fill drop address from order
      if (order.delivery) {
        const addr = order.delivery.address;
        setDropAddress(
          [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.pincode]
            .filter(Boolean)
            .join(', ') || order.delivery.address.addressLine1
        );
        setDropContactName(order.delivery.contactPerson || '');
        setDropContactPhone(order.delivery.contactPhone || '');
      }
    }
  }, [open, order]);

  async function fetchTransportUnits() {
    setIsLoadingUnits(true);
    setError(null);
    try {
      const units = await orderService.getTransportUnits();
      setTransportUnits(units);
      if (units.length === 0) {
        setError('No transport units available. Please contact admin.');
      }
    } catch (err) {
      console.error('Failed to fetch transport units:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transport units');
    } finally {
      setIsLoadingUnits(false);
    }
  }

  async function handleSubmit() {
    if (!selectedUnitId) {
      toast.error('Please select a transport unit');
      return;
    }

    if (!pickupAddress.trim()) {
      toast.error('Please enter pickup address');
      return;
    }

    if (!dropAddress.trim()) {
      toast.error('Please enter drop address');
      return;
    }

    setIsLoading(true);
    try {
      const request: CreateTransportRequestData = {
        transportUnitId: parseInt(selectedUnitId, 10),
        pickupAddress,
        pickupCity,
        pickupState,
        pickupPincode,
        pickupContactName,
        pickupContactPhone,
        dropAddress,
        dropContactName,
        dropContactPhone,
        isUrgent,
        notes,
      };

      const updatedOrder = await orderService.createTransportRequest(order.id, request);
      toast.success('Transport request created successfully');
      onSuccess(updatedOrder);
      onOpenChange(false);
      resetForm();
    } catch (err) {
      console.error('Failed to create transport request:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create transport request');
    } finally {
      setIsLoading(false);
    }
  }

  function resetForm() {
    setSelectedUnitId('');
    setPickupAddress('Biogas Plant, Warehouse A, Industrial Area');
    setPickupCity('');
    setPickupState('');
    setPickupPincode('');
    setPickupContactName('Plant Manager');
    setPickupContactPhone('+91-9876543210');
    setDropAddress('');
    setDropContactName('');
    setDropContactPhone('');
    setNotes('');
    setIsUrgent(false);
  }

  const selectedUnit = transportUnits.find(u => u.id.toString() === selectedUnitId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Assign Transport
          </DialogTitle>
          <DialogDescription>
            Select a transport unit and fill in pickup/drop details for order {order.orderNumber}
          </DialogDescription>
        </DialogHeader>

        {isLoadingUnits ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Loading transport units...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Transport Unit Selection */}
            <div className="space-y-2">
              <Label htmlFor="transportUnit" className="flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Transport Unit *
              </Label>
              <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                <SelectTrigger id="transportUnit">
                  <SelectValue placeholder="Select a transport unit" />
                </SelectTrigger>
                <SelectContent>
                  {transportUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{unit.unitName}</span>
                        <span className="text-xs text-muted-foreground">
                          {unit.unitCode} - {unit.address}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedUnit && (
                <div className="text-xs text-muted-foreground mt-1">
                  Contact: {selectedUnit.contactNumber || 'N/A'} | Email: {selectedUnit.email || 'N/A'}
                </div>
              )}
            </div>

            {/* Pickup Details */}
            <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Pickup Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="pickupAddress">Pickup Address *</Label>
                  <Textarea
                    id="pickupAddress"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    placeholder="Enter pickup address"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickupCity">City</Label>
                  <Input
                    id="pickupCity"
                    value={pickupCity}
                    onChange={(e) => setPickupCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickupState">State</Label>
                  <Input
                    id="pickupState"
                    value={pickupState}
                    onChange={(e) => setPickupState(e.target.value)}
                    placeholder="State"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickupContactName">
                    <User className="h-3 w-3 inline mr-1" />
                    Contact Name
                  </Label>
                  <Input
                    id="pickupContactName"
                    value={pickupContactName}
                    onChange={(e) => setPickupContactName(e.target.value)}
                    placeholder="Contact person name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickupContactPhone">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Contact Phone
                  </Label>
                  <Input
                    id="pickupContactPhone"
                    value={pickupContactPhone}
                    onChange={(e) => setPickupContactPhone(e.target.value)}
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>
              </div>
            </div>

            {/* Drop Details */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Drop Details (From Order)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="dropAddress">Drop Address *</Label>
                  <Textarea
                    id="dropAddress"
                    value={dropAddress}
                    onChange={(e) => setDropAddress(e.target.value)}
                    placeholder="Enter drop address"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dropContactName">
                    <User className="h-3 w-3 inline mr-1" />
                    Recipient Name
                  </Label>
                  <Input
                    id="dropContactName"
                    value={dropContactName}
                    onChange={(e) => setDropContactName(e.target.value)}
                    placeholder="Recipient name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dropContactPhone">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Recipient Phone
                  </Label>
                  <Input
                    id="dropContactPhone"
                    value={dropContactPhone}
                    onChange={(e) => setDropContactPhone(e.target.value)}
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>
              </div>
            </div>

            {/* Additional Options */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isUrgent"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isUrgent" className="text-sm font-medium">
                  Mark as Urgent Delivery
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Special Instructions (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special handling instructions..."
                  rows={2}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingUnits || !selectedUnitId || !!error}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Truck className="mr-2 h-4 w-4" />
                Create Transport Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
